import React from 'react'
import { Alert, Typography } from 'antd'
import {
  IBroadcastAudience,
  IBroadcastSellers,
  IBroadcastUsers
} from 'models/notifications'
import styles from '../Notifications.module.scss'
import {
  addresses,
  chatsCount,
  devices,
  formatDateTime,
  formatRub,
  getEmailDuration,
  getSegmentLines,
  getSmsSegments,
  phonesCount,
  sellersCount,
  sellersGenitive,
  smsCount
} from './broadcast'

interface BroadcastSummaryProps {
  audience: IBroadcastAudience
  sellers: IBroadcastSellers
  users: IBroadcastUsers
  smsText?: string | null
  linkLabel?: string | null
  scheduledAt?: string | null
  quietHours?: boolean
}

export const BroadcastSummary: React.FC<BroadcastSummaryProps> = ({
  audience,
  sellers,
  users,
  smsText,
  linkLabel,
  scheduledAt,
  quietHours
}) => {
  const smsSegments = getSmsSegments(smsText?.trim() ?? '')
  const sellerLines = [
    sellers.push && `Push — ${devices(audience.sellers.push)}`,
    sellers.bot &&
      `Seller-бот — ${sellersCount(audience.sellers.bot)} (${chatsCount(
        audience.sellers.bot_telegram + audience.sellers.bot_max
      )})`,
    sellers.email && `E-mail — ${addresses(audience.sellers.email)}`,
    sellers.sms &&
      `SMS — ${phonesCount(audience.sellers.sms)} × ${smsCount(smsSegments)}`,
    sellers.popup &&
      `Всплывающее окно — ${sellersCount(audience.sellers.popup)}${
        sellers.popup_notify_new_sellers ? ', и новым тоже' : ', только текущим'
      }`
  ].filter(Boolean)
  const userLines = [
    users.push && `Push — ${devices(audience.users.push)}`,
    users.email && `E-mail — ${addresses(audience.users.email)}`
  ].filter(Boolean)
  const segmentLines = getSegmentLines(users.segments)
  const emails = sellers.email || users.email ? audience.total.email : 0
  const hasNightChannels = Boolean(sellers.push || sellers.bot || sellers.sms)
  const night = hasNightChannels ? audience.sellers.night : 0
  const smsCost = sellers.sms ? audience.sms_cost : null
  const smsBalance = audience.sms_balance
  const isBalanceShort =
    smsCost !== null && smsBalance !== null && smsCost.total_cost > smsBalance

  return (
    <div className={styles.summary}>
      {sellerLines.length > 0 && (
        <>
          <Typography.Text strong>Продавцам</Typography.Text>
          <ul>
            {sellerLines.map((line) => (
              <li key={String(line)}>{line}</li>
            ))}
          </ul>
        </>
      )}
      {userLines.length > 0 && (
        <>
          <Typography.Text strong>
            Пользователям
            {users.marketing_only ? ' (только с согласием)' : ''}
          </Typography.Text>
          {segmentLines.length > 0 && (
            <Typography.Text type='secondary' className={styles.summaryNote}>
              Условия: {segmentLines.join(', ')}
            </Typography.Text>
          )}
          <ul>
            {userLines.map((line) => (
              <li key={String(line)}>{line}</li>
            ))}
          </ul>
        </>
      )}
      {linkLabel && (
        <Typography.Paragraph className={styles.summaryLink}>
          Ссылка: {linkLabel}
        </Typography.Paragraph>
      )}
      <Typography.Paragraph>
        {scheduledAt
          ? `Отправка ${formatDateTime(scheduledAt)}.`
          : 'Отправка сразу.'}
        {night > 0 &&
          (quietHours
            ? ` У ${sellersGenitive(night)} ${
                scheduledAt ? 'в это время' : 'сейчас'
              } ночь — пуш, бот и SMS им уйдут в 9:00 по их времени.`
            : ` У ${sellersGenitive(night)} ${
                scheduledAt ? 'в это время' : 'сейчас'
              } ночь, тихие часы выключены — сообщения придут ночью.`)}
      </Typography.Paragraph>
      {sellers.sms && (
        <Typography.Paragraph>
          {smsCost
            ? `Стоимость SMS ≈ ${formatRub(smsCost.total_cost)} за ${smsCount(
                smsCost.total_sms
              )}`
            : 'Стоимость SMS sms.ru не сообщил'}
          {smsBalance !== null
            ? `, баланс sms.ru ${formatRub(smsBalance)}.`
            : ', баланс sms.ru не получен.'}
        </Typography.Paragraph>
      )}
      {isBalanceShort && (
        <Alert
          type='error'
          showIcon
          className={styles.summaryAlert}
          message='Баланса sms.ru не хватит на все SMS — пополните счёт, иначе часть продавцов сообщение не получит.'
        />
      )}
      {emails > 0 && (
        <Typography.Paragraph type='secondary'>
          Писем всего {emails.toLocaleString('ru-RU')} — отправка займёт{' '}
          {getEmailDuration(emails)}.
        </Typography.Paragraph>
      )}
      {users.email && !users.marketing_only && (
        <Alert
          type='warning'
          showIcon
          message='Письма получат и пользователи, не дававшие согласия на рассылку. Рекламу без согласия закон запрещает (ст. 18 38-ФЗ).'
        />
      )}
    </div>
  )
}
